import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { vibrate } from "@/lib/haptics";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg text-sm font-medium ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 active:scale-[0.98]",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 hover:shadow-primary/30",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-lg shadow-destructive/20",
        outline: "border border-border bg-transparent hover:bg-secondary/80 hover:text-secondary-foreground hover:border-secondary",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-secondary/80 hover:text-secondary-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        crypto: "bg-primary/10 text-primary border border-primary/25 hover:bg-primary/20 hover:border-primary/40 shadow-sm hover:shadow-md",
        warning: "bg-warning/10 text-warning border border-warning/25 hover:bg-warning/20 hover:border-warning/40 shadow-lg shadow-warning/10",
        success: "bg-success/10 text-success border border-success/25 hover:bg-success/20 hover:border-success/40 shadow-lg shadow-success/10",
        glass: "glass border border-border/50 text-foreground hover:bg-secondary/50 hover:border-border",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 rounded-lg px-4 text-xs",
        lg: "h-12 rounded-xl px-8",
        xl: "h-14 rounded-xl px-10 text-base font-semibold",
        icon: "h-10 w-10 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, onClick, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      vibrate(8, 'light');
      onClick?.(e);
    };
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} onClick={!asChild ? handleClick : onClick} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
